<template>
	<div class="ci-business-type non_selectable">
		<div v-if="classification_warning" class="bt-warning" role="alert">
			<strong class="bt-warning__title">La clasificación no calza con el tipo de negocio</strong>
			<span class="bt-warning__text">{{ classification_warning }}</span>
		</div>

		<section class="bt-block">
			<p class="bt-eyebrow">Clasificación</p>
			<h2 class="bt-heading">Tipo de negocio</h2>

			<div class="bt-groups">
				<div v-for="row in super_types" :key="row" class="bt-group">
					<span class="bt-group__title">{{ row }}</span>
					<ul class="bt-options">
						<li v-for="subrow in basic_data.all_business_types[row]" :key="subrow.name"
							class="bt-option">
							<input type="checkbox" class="bt-checkbox"
								:id="'bt_' + subrow.super_type + '_n_' + subrow.name"
								v-model="checked[subrow.super_type + '_' + subrow.name]" />
							<span class="bt-option__name" @click="toggle(subrow.super_type + '_' + subrow.name)">
								{{ subrow.name }}
							</span>
						</li>
					</ul>
				</div>
			</div>
		</section>

		<section class="bt-block bt-classify">
			<div>
				<p class="bt-eyebrow">Ventas</p>
				<h2 class="bt-heading">¿Esta empresa debe clasificar las ventas?</h2>
			</div>
			<div class="bt-toggle">
				<input class="bt-checkbox" type="checkbox" id="bt_classify_by_sales"
					v-model="classify_by_sales" />
				<button type="button" class="bt-chip" :class="{ 'bt-chip--on': classify_by_sales }"
					@click="toggle_classify()">
					{{ classify_by_sales ? 'Si' : 'No' }}
				</button>
			</div>
		</section>

		<div class="bt-actions">
			<b-button class="bt-btn-primary" @click="grabarTiposNegocio()">
				Grabar
			</b-button>
		</div>
	</div>
</template>

<script>
import { mapActions } from "vuex";
import {
	consoleHandlerError,
	extractErrorMessage,
	notificate,
} from "@/utils/message";

export default {
	name: "CompanyInfoBusinessType",
	props: {
		basicData: {
			type: Object,
			required: true,
			default: () => ({})
		}
	},
	data() {
		return {
			basic_data: {},
			super_types: [],
			checked: {},
			classify_by_sales: false,
			// Warning del validador super_type ↔ clasificación (H1/F6);
			// null = sin mismatch (o check aún no cargado).
			classification_warning: null,
		};
	},
	methods: {
		...mapActions("analystCompany", ["saveBusinessType", "fetchBusinessTypeCheck"]),
		async refreshClassificationCheck() {
			if (!this.basic_data.companyId) return;
			try {
				const check = await this.fetchBusinessTypeCheck(this.basic_data.companyId);
				this.classification_warning = check.mismatch ? check.warning : null;
			} catch (error) {
				// El check es informativo: si falla, el tab sigue operable.
				this.classification_warning = null;
				consoleHandlerError({ error, silent: true });
			}
		},
		async grabarTiposNegocio() {
			try {
				const data = await this.saveBusinessType({
					companyId: this.basic_data.companyId,
					checked: this.checked,
					classifyBySales: this.classify_by_sales,
				});
				notificate(data['status'], "is-success is-light");
				this.basicData.classify_by_sales = this.classify_by_sales;
				await this.refreshClassificationCheck();
			} catch (error) {
				// El backend ahora devuelve 400/404 (o 500) con {detail} en error.
				notificate(
					extractErrorMessage(error, "No se pudieron guardar los tipos de negocio."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		toggle_classify() {
			this.classify_by_sales = !this.classify_by_sales;
		},
		toggle(key) {
			const newChecked = { ...this.checked };
			newChecked[key] = !newChecked[key];
			this.checked = newChecked;
		}
	},
	watch: {
		basicData: {
			handler(data) {
				this.basic_data = data;
				this.classify_by_sales = this.basic_data.classify_by_sales;
				this.super_types = [];
				for (const [superType, names] of Object.entries(this.basic_data.all_business_types)) {
					this.super_types.push(superType)
					for (const elem of names) {
						this.checked[elem.super_type + '_' + elem.name] = false;
					}
				}
				for (const names of Object.values(this.basic_data.business_types)) {
					for (const elem of names) {
						this.checked[elem.super_type + '_' + elem.name] = true;
					}
				}
				this.refreshClassificationCheck();
			},
			immediate: true,
		},
	}
};
</script>

<style scoped lang="scss">
.ci-business-type {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.non_selectable {
	user-select: none;
}

.bt-warning {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 14px 16px;
	background: var(--ap-warn-wash);
	border: 1px solid var(--ap-warn);
	border-radius: var(--ap-radius-sm);

	&__title {
		font-size: 13px;
		font-weight: 700;
		color: var(--ap-warn-ink);
	}

	&__text {
		font-size: 13px;
		line-height: 1.5;
		color: var(--ap-ink-2);
	}
}

.bt-eyebrow {
	margin: 0 0 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.bt-heading {
	margin: 0;
	font-size: 17px;
	font-weight: 700;
	color: var(--ap-ink);
}

.bt-block {
	background: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	padding: 18px;
}

.bt-groups {
	margin-top: 16px;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.bt-group {
	background: var(--ap-paper);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	padding: 14px 16px;

	&__title {
		display: block;
		font-size: 13px;
		font-weight: 700;
		color: var(--ap-ink);
		margin-bottom: 10px;
	}
}

.bt-options {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.bt-option {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 4px 0;

	&__name {
		font-size: 14px;
		color: var(--ap-ink-2);
		cursor: pointer;
		transition: color 0.16s var(--ap-ease);

		&:hover {
			color: var(--ap-teal);
		}
	}
}

.bt-checkbox {
	width: 18px;
	height: 18px;
	margin: 0;
	accent-color: var(--ap-teal);
	cursor: pointer;
	flex: 0 0 auto;

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

.bt-classify {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
}

.bt-toggle {
	display: flex;
	align-items: center;
	gap: 12px;
}

.bt-chip {
	min-width: 72px;
	height: 36px;
	padding: 0 18px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-family: var(--ap-font);
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	border-radius: var(--ap-radius-pill);
	border: 1px solid var(--ap-line-strong);
	background: var(--ap-paper);
	color: var(--ap-slate);
	transition: background 0.16s var(--ap-ease), border-color 0.16s var(--ap-ease),
		color 0.16s var(--ap-ease);

	&:hover {
		border-color: var(--ap-teal);
		color: var(--ap-teal);
	}

	&--on {
		background: var(--ap-teal-wash);
		border-color: var(--ap-teal);
		color: var(--ap-teal-deep);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

.bt-actions {
	display: flex;
	justify-content: flex-end;
}

.bt-btn-primary {
	&.button {
		height: 40px;
		padding: 0 18px;
		border-radius: var(--ap-radius-pill);
		border: none;
		background: var(--ap-ink);
		color: var(--ap-paper);
		font-family: var(--ap-font);
		font-weight: 600;
		transition: background 0.16s var(--ap-ease), transform 0.16s var(--ap-ease);

		&:hover {
			background: var(--ap-teal-deep);
			color: var(--ap-paper);
			transform: translateY(-1px);
		}

		&:focus-visible {
			outline: 2px solid var(--ap-teal);
			outline-offset: 2px;
		}
	}
}
</style>
